import { useEffect, useMemo, useState } from 'react'
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom'

import { api } from '../api/client'
import type {
  CodeProjectRow,
  GateDecision,
  RegistryArtifactRow,
  SupplyComparison,
  SupplyComponentRow,
  SupplyConnection,
  SupplyOverview,
  SupplyPipelineRow,
  SupplyVulnerabilityRow,
  VexRow,
} from '../api/types'
import { Card, EmptyState, ErrorBanner, Spinner, StatCard } from '../components/common'
import { Connessioni } from '../components/Connessioni'
import { DataTable, type Column } from '../components/DataTable'
import { LinkKev } from '../components/Kev'
import { FinestraModifica, StatoRunner, TabellaModifiche } from '../components/ModificheGitlab'
import { FinestraVex, TabellaDecisioni, TabellaVex, type SoggettoVex } from '../components/CancelloSicurezza'
import { nonLicenziato, NotaLicenzaSbom } from '../components/Sbom'
import {
  BadgeGravita,
  BadgePipeline,
  etichettaFonte,
  etichettaRegistro,
  LinkEsterno,
  motivoFonte,
  quando,
  Soggetto,
  StatoConnessione,
  TabFonti,
} from '../components/SoftwareProdotto'
import { usePermessiModuli } from '../auth/permessi'
import { useAsync } from '../hooks/useAsync'
import { localeCorrente, t } from '../i18n/lingua'

/**
 * Inventario Software → Software prodotto: what the organisation builds and
 * publishes (GitLab projects and pipelines, Artifactory and Nexus artifacts),
 * the SBOM of each build and its vulnerabilities. Every source in its own tab,
 * exactly as it reports, plus «Confronto» for the double check. The view, the
 * source and the filters are in the URL. See supply_chain_service/views.
 */

type Vista = 'panoramica' | 'progetti' | 'pipeline' | 'registri' | 'componenti' | 'vulnerabilita' | 'cancello' | 'vex' | 'modifiche' | 'connessioni'

const VISTE: Array<{ key: Vista; label: () => string }> = [
  { key: 'panoramica', label: () => t('Panoramica') },
  { key: 'progetti', label: () => t('Progetti') },
  { key: 'pipeline', label: () => t('Pipeline') },
  { key: 'registri', label: () => t('Registri') },
  { key: 'componenti', label: () => t('Componenti') },
  { key: 'vulnerabilita', label: () => t('Vulnerabilità') },
  { key: 'cancello', label: () => t('Cancello') },
  { key: 'vex', label: () => t('VEX') },
  { key: 'modifiche', label: () => t('Modifiche') },
  { key: 'connessioni', label: () => t('Connessioni') },
]

const n = (x: number) => x.toLocaleString(localeCorrente())

/** Each view has its own address (a menu item of the DevSecOps area). */
const PERCORSI: Record<Vista, string> = {
  panoramica: '', progetti: 'projects', pipeline: 'pipelines', registri: 'registries', componenti: 'components',
  vulnerabilita: 'vulnerabilities', cancello: 'gate', vex: 'vex', modifiche: 'changes', connessioni: 'connections',
}

export function percorsoVista(v: Vista): string {
  return PERCORSI[v] ? `/software-supply/${PERCORSI[v]}` : '/software-supply'
}

export default function SoftwareProdotto() {
  const [params] = useSearchParams()
  const { vista: segmento = '' } = useParams()
  const naviga = useNavigate()
  const vista = ((Object.keys(PERCORSI) as Vista[]).find((v) => PERCORSI[v] === segmento) ?? 'panoramica') as Vista
  const panoramica = useAsync(() => api.supplyOverview(), [])
  const scrive = usePermessiModuli().scrive('software_supply')
  const [aggiorno, setAggiorno] = useState(false)
  const [errore, setErrore] = useState<unknown>(null)

  const vai = (v: Vista, extra: Record<string, string> = {}) => {
    const q = new URLSearchParams(extra).toString()
    naviga(`${percorsoVista(v)}${q ? `?${q}` : ''}`)
  }
  // The old links (?vista=…) keep working.
  const vecchia = VISTE.find((v) => v.key === params.get('vista'))?.key

  // «Aggiorna»: ask for a pass, then follow it until no connection is busy.
  async function aggiorna() {
    setErrore(null)
    setAggiorno(true)
    try {
      await api.supplyRefresh()
      for (let i = 0; i < 60; i++) {
        await new Promise((r) => setTimeout(r, 3000))
        const stati = await api.supplyStatus()
        if (!stati.some((c) => c.running || c.requested)) break
      }
      await panoramica.reload()
    } catch (e) {
      setErrore(e)
    } finally {
      setAggiorno(false)
    }
  }

  const header = (
    <header className="page__header">
      <div>
        <h1 className="page__title">{t('Software prodotto')}</h1>
        <p className="page__subtitle">
          {t('Il software che l’organizzazione costruisce e pubblica: progetti e pipeline di GitLab, artefatti di Artifactory e Nexus, l’SBOM di ogni build con le sue vulnerabilità e dove l’immagine gira. Ogni fonte ha il suo tab, così com’è; «Confronto» mette le fonti una accanto all’altra.')}
        </p>
      </div>
      {scrive && (
        <button type="button" onClick={() => void aggiorna()} disabled={aggiorno}>
          {aggiorno ? t('Aggiornamento in corso…') : t('Aggiorna')}
        </button>
      )}
    </header>
  )

  if (vecchia) {
    const resto = new URLSearchParams(params)
    resto.delete('vista')
    const q = resto.toString()
    return <Navigate to={`${percorsoVista(vecchia)}${q ? `?${q}` : ''}`} replace />
  }
  if (panoramica.error && nonLicenziato(panoramica.error)) {
    return <>{header}<Card><NotaLicenzaSbom /></Card></>
  }
  const p = panoramica.data

  return (
    <>
      {header}
      <ErrorBanner error={panoramica.error ?? errore} />
      {panoramica.loading && !p ? <Spinner /> : p && (
        <>
          {vista === 'panoramica' && <Panoramica p={p} vai={vai} />}
          {vista === 'progetti' && <Progetti />}
          {vista === 'pipeline' && <Pipeline />}
          {vista === 'registri' && <Registri />}
          {vista === 'componenti' && <Componenti p={p} />}
          {vista === 'vulnerabilita' && <Vulnerabilita p={p} />}
          {vista === 'cancello' && <Cancello p={p} scrive={scrive} />}
          {vista === 'vex' && <Vex scrive={scrive} />}
          {vista === 'modifiche' && <Modifiche />}
          {vista === 'connessioni' && (
            <Card title={t('Connessioni')}>
              {p.connections.map((c) => (
                <div key={c.id} style={{ marginBottom: 10 }}>
                  <StatoConnessione c={c} />
                  {c.kind === 'gitlab' && <RunnerRenovate c={c} scrive={scrive} />}
                </div>
              ))}
              <Connessioni funzioni={['sbom']} segnaposto="gitlab-azienda" onChanged={() => void panoramica.reload()} />
            </Card>
          )}
        </>
      )}
    </>
  )
}

/**
 * A GitLab connection's Renovate runner: its state and «Prepara il runner»,
 * the guided MR that adds CENSO's on-demand job (or a whole runner) to the
 * project chosen here — the one found by the last pass, by default.
 */
function RunnerRenovate({ c, scrive }: { c: SupplyConnection; scrive: boolean }) {
  const r = c.renovate_runner
  const [progetti, setProgetti] = useState<CodeProjectRow[] | null>(null)
  const [scelto, setScelto] = useState('')
  const [aperto, setAperto] = useState<{ id: string; path: string } | null>(null)
  const [esito, setEsito] = useState<{ testo: string; url?: string } | null>(null)
  const [errore, setErrore] = useState<unknown>(null)

  async function scegli() {
    setErrore(null)
    try {
      const tutti = (await api.supplyProjects()).filter((x) => x.connection_id === c.id && !x.archived)
      setProgetti(tutti)
      setScelto(r?.project_id ?? tutti.find((x) => x.path === r?.path)?.id ?? tutti.find((x) => /renovate/i.test(x.path))?.id ?? '')
    } catch (e) {
      setErrore(e)
    }
  }

  const progetto = progetti?.find((x) => x.id === scelto)
  return (
    <div style={{ margin: '4px 0 0' }}>
      <small><StatoRunner r={r} /></small>
      <ErrorBanner error={errore} />
      {esito && <div className="banner banner--info">{esito.testo}{esito.url && <> <LinkEsterno href={esito.url}>{t('Apri in GitLab')}</LinkEsterno></>}</div>}
      {scrive && progetti === null && (
        <div className="toolbar" style={{ margin: '4px 0 0' }}>
          <button type="button" className="secondary" onClick={() => void scegli()}>
            {r?.on_demand ? t('Aggiorna il runner di Renovate') : t('Prepara il runner di Renovate')}
          </button>
        </div>
      )}
      {progetti !== null && (
        progetti.length === 0 ? <p className="text-muted">{t('Nessun progetto letto da questa connessione: aspetta il primo giro.')}</p> : (
          <div className="toolbar" style={{ alignItems: 'flex-end', margin: '4px 0 0' }}>
            <div className="field" style={{ flex: '0 1 520px', minWidth: 240 }}>
              <label htmlFor={`runner-${c.id}`}>{t('Progetto del runner')}</label>
              <select id={`runner-${c.id}`} value={scelto} onChange={(e) => setScelto(e.target.value)}>
                <option value="">{t('— scegli —')}</option>
                {progetti.map((x) => <option key={x.id} value={x.id}>{x.path}</option>)}
              </select>
              <div className="field__hint">{t('Un progetto dedicato, per esempio «renovate-runner»: il job gira lì con il token del bot. Indicalo anche nella connessione («Progetto del runner Renovate») perché CENSO lo usi.')}</div>
            </div>
            <button type="button" disabled={!progetto} onClick={() => progetto && setAperto({ id: progetto.id, path: progetto.path })}>{t('Continua')}</button>
            <button type="button" className="secondary" onClick={() => setProgetti(null)}>{t('Annulla')}</button>
          </div>
        )
      )}
      {aperto && (
        <FinestraModifica progetto={aperto} tipo="renovate_runner" onChiudi={() => setAperto(null)}
          onFatto={(testo, url) => { setAperto(null); setProgetti(null); setEsito({ testo, url }) }} />
      )}
    </div>
  )
}

function Panoramica({ p, vai }: { p: SupplyOverview; vai: (v: Vista, extra?: Record<string, string>) => void }) {
  if (p.connections.length === 0) {
    return (
      <Card>
        <EmptyState title={t('Nessuna connessione ancora')}
          hint={t('Collega GitLab, Artifactory o Nexus dalla scheda «Connessioni»: CENSO legge in sottofondo progetti, pipeline e artefatti, e l’SBOM che le pipeline producono.')} />
        <div className="toolbar"><button type="button" onClick={() => vai('connessioni')}>{t('Aggiungi una connessione')}</button></div>
      </Card>
    )
  }
  const pr = p.projects
  return (
    <>
      <div className="stats">
        <StatCard label={t('Progetti')} value={n(pr.total)} onClick={() => vai('progetti')} />
        <StatCard label={t('Con SBOM dalla pipeline')} value={n(pr.with_sbom)} tone={pr.with_sbom ? 'ok' : undefined}
          onClick={() => vai('progetti', { filtro: 'con_sbom' })} />
        <StatCard label={t('Senza SBOM')} value={n(pr.without_sbom)} tone={pr.without_sbom ? 'warn' : 'ok'}
          onClick={() => vai('progetti', { filtro: 'senza_sbom' })} />
        <StatCard label={t('Con librerie vulnerabili')} value={n(pr.vulnerable)} tone={pr.vulnerable ? 'warn' : 'ok'}
          onClick={() => vai('progetti', { filtro: 'vulnerabili' })} />
        <StatCard label={t('Con librerie sfruttate attivamente')} value={n(pr.exploited)} tone={pr.exploited ? 'error' : 'ok'}
          onClick={() => vai('vulnerabilita', { fonte: 'censo' })} />
        <StatCard label={t('Ultima pipeline fallita')} value={n(pr.failed_pipeline)} tone={pr.failed_pipeline ? 'warn' : 'ok'}
          onClick={() => vai('progetti', { filtro: 'fallite' })} />
        <StatCard label={t('MR di sicurezza di Renovate aperte')} value={n(pr.renovate_security_mrs)}
          hint={t('{n} progetti con Renovate', { n: pr.renovate })} onClick={() => vai('progetti', { filtro: 'renovate' })} />
        <StatCard label={t('Artefatti nei registri')} value={n(p.artifacts.total)} onClick={() => vai('registri')} />
        <StatCard label={t('Immagini in esecuzione')} value={n(p.artifacts.running_images)}
          hint={t('su {n} immagini pubblicate', { n: p.artifacts.images })} onClick={() => vai('registri', { filtro: 'in_esecuzione' })} />
        <StatCard label={t('Componenti non valutati')} value={n(p.components.unevaluated)}
          hint={t('ecosistemi senza catalogo: non vuol dire «nessuna vulnerabilità»')}
          onClick={() => vai('componenti')} />
      </div>
      <Card title={t('Cancello di sicurezza e VEX (ultimi 7 giorni)')}>
      <div className="stats" style={{ marginBottom: 0 }}>
        <StatCard label={t('Progetti con il cancello')} value={n(p.gate.enabled)} onClick={() => vai('cancello')} />
        <StatCard label={t('Pipeline bloccate')} value={n(p.gate.fail_7d)} tone={p.gate.fail_7d ? 'error' : 'ok'}
          onClick={() => vai('cancello', { esito: 'fail' })} />
        <StatCard label={t('Pipeline con avviso')} value={n(p.gate.warn_7d)} tone={p.gate.warn_7d ? 'warn' : 'ok'}
          onClick={() => vai('cancello', { esito: 'warn' })} />
        <StatCard label={t('Pipeline superate')} value={n(p.gate.pass_7d)} onClick={() => vai('cancello', { esito: 'pass' })} />
        <StatCard label={t('Non decise')} value={n(p.gate.undecided_7d)} tone={p.gate.undecided_7d ? 'warn' : undefined}
          hint={t('nessun SBOM, GitLab non raggiungibile…')} onClick={() => vai('cancello', { esito: 'non_decise' })} />
        <StatCard label={t('Valutazioni VEX in vigore')} value={n(p.gate.vex_active)} onClick={() => vai('vex')} />
        <StatCard label={t('VEX in scadenza (30 giorni)')} value={n(p.gate.vex_expiring)} tone={p.gate.vex_expiring ? 'warn' : undefined}
          onClick={() => vai('vex', { filtro: 'in_scadenza' })} />
        <StatCard label={t('VEX scadute')} value={n(p.gate.vex_expired)} tone={p.gate.vex_expired ? 'warn' : undefined}
          hint={t('il cancello le conta di nuovo')} onClick={() => vai('vex', { filtro: 'scadute' })} />
      </div>
      </Card>
      <Card title={t('Connessioni e giri in sottofondo')}>
        {p.connections.map((c) => <div key={c.id} style={{ marginBottom: 10 }}><StatoConnessione c={c} /></div>)}
        <p className="field__hint" style={{ marginBottom: 0 }}>
          {t('GitLab si rilegge ogni 30 minuti (solo i progetti con attività), Artifactory ogni 2 ore, Nexus ogni 6 ore entro il tetto di richieste giornaliero. Le chiamate partono una alla volta, dalla sonda o dal Control Plane come scelto nella connessione.')}
        </p>
      </Card>
    </>
  )
}

// ------------------------------------------------------------------ projects

type FiltroProgetti = 'tutti' | 'con_sbom' | 'senza_sbom' | 'vulnerabili' | 'fallite' | 'senza_renovate' | 'renovate'

function nelFiltro(r: CodeProjectRow, f: FiltroProgetti): boolean {
  switch (f) {
    case 'con_sbom': return Boolean(r.sbom)
    case 'senza_sbom': return !r.sbom && !r.archived
    case 'vulnerabili': return (r.sbom?.vulnerable ?? 0) > 0
    case 'fallite': return r.last_pipeline?.status === 'failed'
    case 'senza_renovate': return !r.renovate.file
    case 'renovate': return r.renovate.security_mrs > 0
    default: return true
  }
}

function Segmenti<T extends string>({ valori, attivo, onScegli }: {
  valori: Array<[T, string, number]>; attivo: T; onScegli: (v: T) => void
}) {
  return (
    <div className="toolbar" style={{ flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
      {valori.map(([v, etichetta, c]) => (
        <button key={v} type="button" className={attivo === v ? '' : 'secondary'} onClick={() => onScegli(v)}>
          {etichetta} ({n(c)})
        </button>
      ))}
    </div>
  )
}

function Progetti() {
  const [params, setParams] = useSearchParams()
  const filtro = (params.get('filtro') ?? 'tutti') as FiltroProgetti
  const dati = useAsync(() => api.supplyProjects(), [])
  const righe = dati.data ?? []
  const scegli = (f: FiltroProgetti) => setParams(f === 'tutti' ? {} : { filtro: f })
  const conta = (f: FiltroProgetti) => righe.filter((r) => nelFiltro(r, f)).length
  const colonne: Column<CodeProjectRow>[] = [
    { key: 'path', header: t('Progetto'), value: (r) => r.path, render: (r) => (
      <>
        <Link to={`/software-supply/projects/${r.id}`}><strong>{r.path}</strong></Link>
        {r.archived && <> <span className="badge badge--muted">{t('archiviato')}</span></>}
        {r.web_url && <><br /><small><LinkEsterno href={r.web_url}>{t('Apri in GitLab')}</LinkEsterno></small></>}
      </>
    ) },
    { key: 'default_branch', header: t('Ramo principale'), mono: true, value: (r) => r.default_branch },
    { key: 'pipeline', header: t('Ultima pipeline'), value: (r) => r.last_pipeline?.status ?? '', render: (r) => (
      <><BadgePipeline stato={r.last_pipeline?.status} />{r.last_pipeline?.created_at && <><br /><small className="text-muted">{quando(r.last_pipeline.created_at)}</small></>}</>
    ) },
    { key: 'sbom', header: t('SBOM'), value: (r) => r.sbom?.components ?? -1, render: (r) => (r.sbom ? (
      <>
        <small>{t('{c} componenti', { c: r.sbom.components })}</small>
        {r.sbom.vulnerable > 0 && <> <span className="badge badge--warn">{t('{n} vulnerabili', { n: r.sbom.vulnerable })}</span></>}
        {r.sbom.exploited > 0 && <> <span className="badge badge--error">{t('{n} sfruttate', { n: r.sbom.exploited })}</span></>}
        {r.sbom.unevaluated > 0 && <><br /><small className="text-muted">{t('{n} non valutati', { n: r.sbom.unevaluated })}</small></>}
      </>
    ) : <span className="badge badge--muted">{t('senza SBOM')}</span>) },
    { key: 'renovate', header: t('Renovate'), value: (r) => r.renovate.file, render: (r) => (r.renovate.file ? (
      <>
        <small className="mono">{r.renovate.file}</small><br />
        <small className="text-muted">
          {r.renovate.osv_alerts === true ? t('avvisi OSV accesi') : r.renovate.osv_alerts === false ? t('avvisi OSV spenti') : t('avvisi OSV non determinabili')}
          {r.renovate.open_mrs > 0 && ` · ${t('{n} MR aperte', { n: r.renovate.open_mrs })}`}
        </small>
        {r.renovate.security_mrs > 0 && <> <span className="badge badge--warn">{t('{n} di sicurezza', { n: r.renovate.security_mrs })}</span></>}
      </>
    ) : <span className="text-muted">{t('non configurato')}</span>) },
    { key: 'artifacts', header: t('Artefatti'), type: 'number', value: (r) => r.artifacts },
    { key: 'connection', header: t('Connessione'), value: (r) => r.connection, hidden: true },
    { key: 'sync_error', header: t('Ultima lettura'), value: (r) => r.sync_error || r.synced_at || '', render: (r) => (
      r.sync_error ? <small style={{ color: 'var(--error)' }}>{r.sync_error}</small> : <small className="text-muted">{quando(r.synced_at)}</small>
    ) },
  ]
  return (
    <Card>
      <ErrorBanner error={dati.error} />
      {dati.loading && !dati.data ? <Spinner /> : (
        <>
          <Segmenti attivo={filtro} onScegli={scegli} valori={[
            ['tutti', t('Tutti'), righe.length], ['con_sbom', t('Con SBOM'), conta('con_sbom')],
            ['senza_sbom', t('Senza SBOM'), conta('senza_sbom')], ['vulnerabili', t('Vulnerabili'), conta('vulnerabili')],
            ['fallite', t('Pipeline fallita'), conta('fallite')], ['senza_renovate', t('Senza Renovate'), conta('senza_renovate')],
            ['renovate', t('MR di sicurezza aperte'), conta('renovate')],
          ]} />
          <DataTable rows={righe.filter((r) => nelFiltro(r, filtro))} rowKey={(r) => r.id} columns={colonne}
            conteggio={{ nome: t('progetti') }} emptyText={t('Nessun progetto in questo filtro.')} />
        </>
      )}
    </Card>
  )
}

function Pipeline() {
  const dati = useAsync(() => api.supplyPipelines(), [])
  const colonne: Column<SupplyPipelineRow>[] = [
    { key: 'project', header: t('Progetto'), value: (r) => r.project,
      render: (r) => <Link to={`/software-supply/projects/${r.project_id}`}>{r.project}</Link> },
    { key: 'ref', header: t('Ramo o tag'), value: (r) => r.ref, render: (r) => (
      <><span className="mono">{r.ref}</span>{r.is_tag && <> <span className="badge badge--info">{t('tag')}</span></>}</>
    ) },
    { key: 'status', header: t('Stato'), value: (r) => r.status, render: (r) => <BadgePipeline stato={r.status} /> },
    { key: 'source', header: t('Avviata da'), value: (r) => r.source },
    { key: 'created_at', header: t('Creata'), type: 'date', value: (r) => r.created_at ?? '', render: (r) => <small>{quando(r.created_at)}</small> },
    { key: 'sbom', header: t('SBOM letto'), value: (r) => (r.sbom ? 1 : 0), render: (r) => (r.sbom ? <span className="badge badge--ok">{t('sì')}</span> : '—') },
    { key: 'web_url', header: '', filter: false, sort: false, render: (r) => (r.web_url ? <small><LinkEsterno href={r.web_url}>{t('Apri')}</LinkEsterno></small> : null) },
  ]
  return (
    <Card>
      <ErrorBanner error={dati.error} />
      {dati.loading && !dati.data ? <Spinner /> : (
        <DataTable rows={dati.data ?? []} rowKey={(r) => `${r.project_id}:${r.id}`} columns={colonne}
          initialSort={{ key: 'created_at', dir: 'desc' }} conteggio={{ nome: t('pipeline') }}
          emptyText={t('Nessuna pipeline letta ancora.')} />
      )}
    </Card>
  )
}

// ---------------------------------------------------------------- registries

function TabellaArtefatti({ righe }: { righe: RegistryArtifactRow[] }) {
  const colonne: Column<RegistryArtifactRow>[] = [
    { key: 'name', header: t('Artefatto'), value: (r) => `${r.group} ${r.name} ${r.version}`, render: (r) => (
      <>
        <strong>{r.group ? `${r.group}:` : ''}{r.name}</strong> <span className="mono">{r.version}</span>
        <br /><small className="text-muted">{etichettaRegistro(r.provider)} · {r.repository} · {r.format}</small>
      </>
    ) },
    { key: 'digest', header: t('Impronta'), value: (r) => r.docker_digest || r.sha256, render: (r) => (
      <small className="mono" title={r.docker_digest || r.sha256}>{(r.docker_digest || r.sha256).replace('sha256:', '').slice(0, 16) || '—'}</small>
    ) },
    { key: 'project', header: t('Progetto'), value: (r) => r.project ?? '', render: (r) => (
      r.project_id ? <Link to={`/software-supply/projects/${r.project_id}`}>{r.project || t('progetto')}</Link> : <span className="text-muted">—</span>
    ) },
    { key: 'has_sbom', header: t('SBOM allegato'), value: (r) => (r.has_sbom ? 1 : 0), render: (r) => (r.has_sbom ? <span className="badge badge--ok">{t('sì')}</span> : '—') },
    { key: 'running', header: t('In esecuzione'), value: (r) => r.running.length, render: (r) => (r.running.length ? (
      <small title={r.running.map((c) => `${c.ci_name} · ${c.container}`).join('\n')}>
        {r.running.slice(0, 2).map((c) => <span key={c.ci_id + c.container}><Link to={`/cis/${c.ci_id}`}>{c.ci_name}</Link> · {c.container}<br /></span>)}
        {r.running.length > 2 && t('e altri {n}', { n: r.running.length - 2 })}
      </small>
    ) : <span className="text-muted">—</span>) },
    { key: 'created_at', header: t('Pubblicato'), type: 'date', value: (r) => r.created_at ?? '', render: (r) => <small>{quando(r.created_at)}</small> },
    { key: 'last_downloaded', header: t('Ultimo download'), type: 'date', value: (r) => r.last_downloaded ?? '', hidden: true,
      render: (r) => <small>{quando(r.last_downloaded)}</small> },
  ]
  return <DataTable rows={righe} rowKey={(r) => r.id} columns={colonne} conteggio={{ nome: t('artefatti') }}
    initialSort={{ key: 'created_at', dir: 'desc' }} emptyText={t('Nessun artefatto.')} />
}

function Registri() {
  const [params, setParams] = useSearchParams()
  const fonte = params.get('fonte') ?? 'artifactory'
  const soloInEsecuzione = params.get('filtro') === 'in_esecuzione'
  const dati = useAsync(() => (fonte === 'confronto' ? Promise.resolve([]) : api.supplyArtifacts(fonte)), [fonte])
  const confronto = useAsync(() => (fonte === 'confronto' ? api.supplyComparison('artifacts') : Promise.resolve(null)), [fonte])
  const scegli = (f: string) => setParams({ fonte: f })
  const righe = (dati.data ?? []).filter((r) => !soloInEsecuzione || r.running.length > 0)
  return (
    <Card>
      <TabFonti attiva={fonte} onScegli={scegli} fonti={['artifactory', 'nexus', 'gitlab'].map((f) => ({ source: f, available: true, reason: '' }))} />
      {soloInEsecuzione && fonte !== 'confronto' && (
        <p className="field__hint">{t('Solo le immagini in esecuzione sui server.')} <button type="button" className="link" onClick={() => scegli(fonte)}>{t('Mostra tutti')}</button></p>
      )}
      <ErrorBanner error={dati.error ?? confronto.error} />
      {fonte === 'confronto' ? (confronto.data ? <TabellaConfronto c={confronto.data} /> : <Spinner />)
        : dati.loading && !dati.data ? <Spinner /> : <TabellaArtefatti righe={righe} />}
    </Card>
  )
}

// -------------------------------------------------- components and vulnerabilities

function Componenti({ p }: { p: SupplyOverview }) {
  const [params, setParams] = useSearchParams()
  const prima = p.sources.components.find((f) => f.available)?.source ?? 'gitlab_pipeline'
  const fonte = params.get('fonte') ?? prima
  const q = params.get('q') ?? undefined
  const stato = p.sources.components.find((f) => f.source === fonte)
  const dati = useAsync(() => (fonte === 'confronto' ? Promise.resolve(null) : api.supplyComponents(fonte)), [fonte])
  const confronto = useAsync(() => (fonte === 'confronto' ? api.supplyComparison('components') : Promise.resolve(null)), [fonte])
  const colonne: Column<SupplyComponentRow>[] = [
    { key: 'subject', header: t('Progetto o artefatto'), value: (r) => r.subject.label, render: (r) => <Soggetto s={r.subject} /> },
    { key: 'name', header: t('Componente'), value: (r) => `${r.name} ${r.purl}`, render: (r) => (
      <><strong>{r.name}</strong> <span className="mono">{r.version}</span><br /><small className="text-muted">{r.ecosystem}</small></>
    ) },
    { key: 'licenses', header: t('Licenze'), value: (r) => r.licenses.join(', ') },
    { key: 'scope', header: t('Dipendenza'), value: (r) => r.scope },
    { key: 'vulnerabilities', header: t('Vulnerabilità'), value: (r) => r.vulnerabilities, render: (r) => (
      !r.evaluated ? <span className="badge badge--muted" title={t('nessun catalogo per questo ecosistema')}>{t('non valutato')}</span>
        : r.vulnerabilities ? <><BadgeGravita g={r.severity} /> <small>{r.vulnerabilities}</small>{r.exploited && <> <LinkKev /></>}</> : '—'
    ) },
  ]
  return (
    <Card>
      <TabFonti attiva={fonte} onScegli={(f) => setParams({ fonte: f })} fonti={p.sources.components} />
      <ErrorBanner error={dati.error ?? confronto.error} />
      {fonte === 'confronto' ? (confronto.data ? <TabellaConfronto c={confronto.data} /> : <Spinner />)
        : stato && !stato.available ? <EmptyState title={t('Fonte non disponibile')} hint={motivoFonte(stato.reason)} />
          : dati.loading && !dati.data ? <Spinner /> : (
            <>
              <p className="field__hint" style={{ marginTop: 0 }}>
                {t('I componenti come li dà {fonte}, senza unirli alle altre fonti: lo stesso componente visto da più fonti compare in ciascun tab, e nel «Confronto» una volta sola.', { fonte: etichettaFonte(fonte) })}
                {dati.data?.truncated && ` ${t('Elenco troncato: si mostrano i primi 20.000.')}`}
              </p>
              <DataTable rows={dati.data?.rows ?? []} rowKey={(r) => r.id} columns={colonne} cercaIniziale={q}
                conteggio={{ nome: t('componenti') }} emptyText={t('Nessun componente da questa fonte.')} />
            </>
          )}
    </Card>
  )
}

function Vulnerabilita({ p }: { p: SupplyOverview }) {
  const [params, setParams] = useSearchParams()
  const fonte = params.get('fonte') ?? 'censo'
  const q = params.get('q') ?? undefined
  const stato = p.sources.vulnerabilities.find((f) => f.source === fonte)
  const dati = useAsync(() => (fonte === 'confronto' ? Promise.resolve(null) : api.supplyVulnerabilities(fonte)), [fonte])
  const confronto = useAsync(() => (fonte === 'confronto' ? api.supplyComparison('vulnerabilities') : Promise.resolve(null)), [fonte])
  const colonne: Column<SupplyVulnerabilityRow>[] = [
    { key: 'vuln_id', header: t('Avviso'), value: (r) => `${r.vuln_id} ${r.cves.join(' ')}`, render: (r) => (
      <>
        {fonte === 'censo'
          ? <a href={`https://osv.dev/vulnerability/${encodeURIComponent(r.vuln_id)}`} target="_blank" rel="noreferrer noopener" className="mono">{r.vuln_id}</a>
          : r.url ? <LinkEsterno href={r.url}><span className="mono">{r.vuln_id}</span></LinkEsterno> : <span className="mono">{r.vuln_id}</span>}
        {r.cves.filter((c) => c !== r.vuln_id).length > 0 && <><br /><small className="mono">{r.cves.filter((c) => c !== r.vuln_id).join(', ')}</small></>}
      </>
    ) },
    { key: 'name', header: t('Componente'), value: (r) => `${r.name} ${r.version}`, render: (r) => (
      <><strong>{r.name}</strong> <span className="mono">{r.version}</span></>
    ) },
    { key: 'severity', header: t('Gravità'), value: (r) => r.severity, render: (r) => (
      <><BadgeGravita g={r.severity} />{r.exploited && <> <LinkKev /></>}</>
    ) },
    { key: 'cvss', header: t('CVSS'), type: 'number', value: (r) => r.cvss ?? -1, render: (r) => (r.cvss != null ? r.cvss.toFixed(1) : '—') },
    { key: 'fixed', header: t('Corretta in'), mono: true, value: (r) => r.fixed },
    { key: 'subjects', header: t('Dove'), value: (r) => r.subjects.length, render: (r) => (
      <small>
        {r.subjects.slice(0, 3).map((s, i) => <span key={i}><Soggetto s={s} /><br /></span>)}
        {r.subjects.length > 3 && t('e altri {n}', { n: r.subjects.length - 3 })}
      </small>
    ) },
    { key: 'summary', header: t('Descrizione'), value: (r) => r.summary, hidden: true },
  ]
  return (
    <Card>
      <TabFonti attiva={fonte} onScegli={(f) => setParams({ fonte: f })} fonti={p.sources.vulnerabilities} />
      <ErrorBanner error={dati.error ?? confronto.error} />
      {fonte === 'confronto' ? (confronto.data ? <TabellaConfronto c={confronto.data} /> : <Spinner />)
        : stato && !stato.available ? <EmptyState title={t('Fonte non disponibile')} hint={motivoFonte(stato.reason)} />
          : dati.loading && !dati.data ? <Spinner /> : (
            <>
              <p className="field__hint" style={{ marginTop: 0 }}>
                {fonte === 'censo'
                  ? t('Le vulnerabilità che il motore di CENSO trova negli SBOM dei build (catalogo OSV, sfruttate attivamente da CISA KEV), con lo stesso confronto di versioni del software installato.')
                  : t('Le vulnerabilità come le riporta {fonte}, con le sue parole per la gravità: niente è ricalcolato da CENSO.', { fonte: etichettaFonte(fonte) })}
              </p>
              <DataTable rows={dati.data?.rows ?? []} rowKey={(r) => `${r.vuln_id}:${r.name}:${r.version}`} columns={colonne}
                cercaIniziale={q} conteggio={{ nome: t('vulnerabilità') }} emptyText={t('Nessuna vulnerabilità da questa fonte.')} />
            </>
          )}
    </Card>
  )
}

// ---------------------------------------------------------------- comparison

type Esito = 'tutti' | 'disagree' | 'single' | 'agree'

function TabellaConfronto({ c }: { c: SupplyComparison }) {
  const [, setParams] = useSearchParams()
  const [esito, setEsito] = useState<Esito>(c.summary.disagree ? 'disagree' : 'tutti')
  useEffect(() => { setEsito(c.summary.disagree ? 'disagree' : 'tutti') }, [c])
  const righe = useMemo(() => c.rows.filter((r) => esito === 'tutti' || r.outcome === esito), [c, esito])
  type Riga = SupplyComparison['rows'][number]
  const apri = (fonte: string, r: Riga) => setParams({ fonte, q: r.vuln_id ?? r.name })
  const colonne: Column<Riga>[] = [
    { key: 'key', header: c.kind === 'vulnerabilities' ? t('Avviso e componente') : c.kind === 'artifacts' ? t('Artefatto') : t('Componente'),
      value: (r) => `${r.vuln_id ?? ''} ${r.name} ${r.version}`, render: (r) => (
        <>{r.vuln_id && <><span className="mono">{r.vuln_id}</span><br /></>}<strong>{r.name}</strong> <span className="mono">{r.version}</span></>
      ) },
    ...c.sources.map((f): Column<Riga> => ({
      key: f, header: c.kind === 'artifacts' ? etichettaRegistro(f) : etichettaFonte(f), value: (r) => (r.sources[f] ? 1 : 0),
      render: (r) => {
        const x = r.sources[f]
        if (!x) return <span className="text-muted">—</span>
        return (
          <button type="button" className="link" style={{ textAlign: 'left' }} onClick={() => apri(f, r)}
            title={t('Apri nel tab di {fonte}', { fonte: etichettaFonte(f) })}>
            {c.kind === 'vulnerabilities' ? <><BadgeGravita g={x.severity ?? ''} />{x.fixed && <><br /><small className="mono">{x.fixed}</small></>}</>
              : <small>{t('sì')}{x.vulnerabilities ? ` · ${t('{n} vulnerabilità', { n: x.vulnerabilities })}` : ''}</small>}
          </button>
        )
      },
    })),
    { key: 'outcome', header: t('Esito'), value: (r) => r.outcome, render: (r) => (
      r.outcome === 'disagree' ? <span className="badge badge--warn">{t('in disaccordo')}</span>
        : r.outcome === 'single' ? <span className="badge badge--muted">{t('una sola fonte')}</span>
          : <span className="badge badge--ok">{t('d’accordo')}</span>
    ) },
  ]
  return (
    <>
      <p className="field__hint" style={{ marginTop: 0 }}>
        {t('Il double check: ogni riga è un elemento, ogni colonna una fonte. «In disaccordo» = le fonti lo vedono ma con gravità o versione che corregge diverse; «una sola fonte» = gli altri non lo riportano (spesso perché non guardano lo stesso build). Un clic su una cella apre la riga nel tab della sua fonte.')}
        {c.truncated && ` ${t('Elenco troncato: si mostrano i primi 20.000.')}`}
      </p>
      <Segmenti<Esito> attivo={esito} onScegli={setEsito} valori={[
        ['tutti', t('Tutti'), c.rows.length], ['disagree', t('In disaccordo'), c.summary.disagree],
        ['single', t('Una sola fonte'), c.summary.single], ['agree', t('D’accordo'), c.summary.agree],
      ]} />
      <DataTable rows={righe} rowKey={(r) => r.key} columns={colonne} conteggio={{ nome: t('righe') }}
        emptyText={t('Niente in questo filtro.')} />
    </>
  )
}

// ------------------------------------------------------------------ gate

type EsitoCancello = 'tutti' | 'fail' | 'warn' | 'pass' | 'non_decise'

function Cancello({ p, scrive }: { p: SupplyOverview; scrive: boolean }) {
  const [params, setParams] = useSearchParams()
  const dati = useAsync(() => api.supplyGateDecisions(), [])
  const [valuta, setValuta] = useState<{ progetto: string; soggetto: SoggettoVex } | null>(null)
  const esito = (params.get('esito') ?? 'tutti') as EsitoCancello
  const nel = (d: GateDecision, e: EsitoCancello) => e === 'tutti' || (e === 'non_decise' ? Boolean(d.error) : d.outcome === e && !d.error)
  const tutte = dati.data ?? []
  const righe = tutte.filter((d) => nel(d, esito))
  const conta = (e: EsitoCancello) => tutte.filter((d) => nel(d, e)).length
  return (
    <Card>
      <p className="field__hint" style={{ marginTop: 0 }}>
        {t('I verdetti del cancello di sicurezza, pipeline per pipeline: il job censo-gate chiede, CENSO legge pipeline e SBOM da GitLab e decide con la regola del progetto. Il cancello si attiva dal dettaglio di ogni progetto.')}
        {p.gate.enabled === 0 && ` ${t('Nessun progetto ha ancora il cancello attivo.')}`}
      </p>
      <ErrorBanner error={dati.error} />
      <Segmenti<EsitoCancello> valori={[['tutti', t('Tutti'), conta('tutti')], ['fail', t('Bloccate'), conta('fail')],
        ['warn', t('Con avviso'), conta('warn')], ['pass', t('Superate'), conta('pass')], ['non_decise', t('Non decise'), conta('non_decise')]]}
        attivo={esito} onScegli={(e) => setParams(e === 'tutti' ? {} : { esito: e })} />
      {dati.loading && !dati.data ? <Spinner /> : (
        <TabellaDecisioni righe={righe} scrive={scrive} onValuta={(d, v) => setValuta({ progetto: d.project_id, soggetto: {
          vuln_id: v.cves[0] ?? v.vuln_id, aliases: [v.vuln_id, ...v.cves].filter((x) => x !== (v.cves[0] ?? v.vuln_id)),
          ecosystem: v.ecosystem, name: v.component, purl_base: v.purl_base, version: v.version } })} />
      )}
      {valuta && <FinestraVex progettoId={valuta.progetto} soggetto={valuta.soggetto} onChiudi={() => setValuta(null)}
        onFatto={() => setValuta(null)} />}
    </Card>
  )
}

type FiltroVex = 'in_vigore' | 'in_scadenza' | 'scadute' | 'revocate' | 'tutte'

function Vex({ scrive }: { scrive: boolean }) {
  const [params, setParams] = useSearchParams()
  const dati = useAsync(() => api.supplyVex(), [])
  const filtro = (params.get('filtro') ?? 'in_vigore') as FiltroVex
  const nel = (v: VexRow, f: FiltroVex) => ({
    in_vigore: v.active, in_scadenza: v.expires_soon, scadute: !v.active && !v.revoked_at, revocate: Boolean(v.revoked_at), tutte: true,
  } as Record<FiltroVex, boolean>)[f]
  const righe = (dati.data ?? []).filter((v) => nel(v, filtro))
  const conta = (f: FiltroVex) => (dati.data ?? []).filter((v) => nel(v, f)).length
  return (
    <Card>
      <p className="field__hint" style={{ marginTop: 0 }}>
        {t('Le valutazioni VEX di tutti i progetti: cosa l’organizzazione dichiara su una vulnerabilità in un suo componente (stati e giustificazioni di CycloneDX). Scadono sempre; il cancello conta di nuovo quelle scadute o revocate. Si scrivono dal dettaglio del progetto o da un verdetto del cancello.')}
      </p>
      <ErrorBanner error={dati.error} />
      <Segmenti<FiltroVex> valori={[['in_vigore', t('In vigore'), conta('in_vigore')], ['in_scadenza', t('In scadenza'), conta('in_scadenza')],
        ['scadute', t('Scadute'), conta('scadute')], ['revocate', t('Revocate'), conta('revocate')], ['tutte', t('Tutte'), conta('tutte')]]}
        attivo={filtro} onScegli={(f) => setParams(f === 'in_vigore' ? {} : { filtro: f })} />
      {dati.loading && !dati.data ? <Spinner /> : <TabellaVex righe={righe} scrive={scrive} onCambiato={() => void dati.reload()} />}
    </Card>
  )
}

function Modifiche() {
  const dati = useAsync(() => api.supplyChanges(), [])
  return (
    <Card>
      <p className="field__hint" style={{ marginTop: 0 }}>
        {t('Ciò che CENSO ha fatto in GitLab su richiesta: pipeline avviate, riprovate o annullate, merge request aperte (con il loro stato, aggiornato a ogni giro). Ogni azione è anche nell’audit del tenant.')}
      </p>
      <ErrorBanner error={dati.error} />
      {dati.loading && !dati.data ? <Spinner /> : <TabellaModifiche righe={dati.data ?? []} />}
    </Card>
  )
}
